// Real approximate lat/long coordinates for major Indian cities, keyed by
// lowercased city name. Used to plot city markers on the Sales Map's
// state-level drill-down. A city dispatched-to that isn't in this list
// still shows up in the ranked list next to the map, just without a marker
// (see SalesMap.js's "unmapped cities" handling) — coordinates here are
// never invented for a city we don't actually recognize.
const CITY_COORDINATES = {
  // Gujarat (most complete, since this is a Gujarat-based business)
  morbi: [22.8173, 70.8378],
  ahmedabad: [23.0225, 72.5714],
  surat: [21.1702, 72.8311],
  vadodara: [22.3072, 73.1812],
  rajkot: [22.3039, 70.8022],
  bhavnagar: [21.7645, 72.1519],
  jamnagar: [22.4707, 70.0577],
  junagadh: [21.5222, 70.4579],
  gandhinagar: [23.2156, 72.6369],
  anand: [22.5645, 72.9289],
  nadiad: [22.6939, 72.8618],
  mehsana: [23.5880, 72.3693],
  bharuch: [21.7051, 72.9959],
  navsari: [20.9467, 72.9520],
  valsad: [20.5992, 72.9342],
  patan: [23.8493, 72.1266],
  porbandar: [21.6417, 69.6293],
  veraval: [20.9159, 70.3629],
  surendranagar: [22.7196, 71.6369],
  amreli: [21.6032, 71.2216],
  botad: [22.1704, 71.6683],
  gondal: [21.9612, 70.7930],
  jetpur: [21.7549, 70.6216],
  wankaner: [22.6192, 70.9494],
  himatnagar: [23.5991, 72.9635],
  palanpur: [24.1747, 72.4383],
  godhra: [22.7788, 73.6142],
  dahod: [22.8340, 74.2631],
  ankleshwar: [21.6266, 73.0104],
  vapi: [20.3714, 72.9047],

  // Major India cities
  mumbai: [19.0760, 72.8777],
  delhi: [28.7041, 77.1025],
  bengaluru: [12.9716, 77.5946],
  bangalore: [12.9716, 77.5946],
  hyderabad: [17.3850, 78.4867],
  chennai: [13.0827, 80.2707],
  kolkata: [22.5726, 88.3639],
  pune: [18.5204, 73.8567],
  jaipur: [26.9124, 75.7873],
  lucknow: [26.8467, 80.9462],
  kanpur: [26.4499, 80.3319],
  nagpur: [21.1458, 79.0882],
  indore: [22.7196, 75.8577],
  bhopal: [23.2599, 77.4126],
  patna: [25.5941, 85.1376],
  ludhiana: [30.9010, 75.8573],
  agra: [27.1767, 78.0081],
  nashik: [19.9975, 73.7898],
  faridabad: [28.4089, 77.3178],
  meerut: [28.9845, 77.7064],
  rajkot_alt: [22.3039, 70.8022],
  varanasi: [25.3176, 82.9739],
  srinagar: [34.0837, 74.7973],
  amritsar: [31.6340, 74.8723],
  coimbatore: [11.0168, 76.9558],
  kochi: [9.9312, 76.2673],
  guwahati: [26.1445, 91.7362],
  chandigarh: [30.7333, 76.7794],
  thane: [19.2183, 72.9781],
  visakhapatnam: [17.6868, 83.2185],
  bhilai: [21.2094, 81.4285],
  jodhpur: [26.2389, 73.0243],
  raipur: [21.2514, 81.6296],
  kota: [25.2138, 75.8648],
  guntur: [16.3067, 80.4365],
  ranchi: [23.3441, 85.3096],
  gwalior: [26.2183, 78.1828],
  vijayawada: [16.5062, 80.6480],
  jabalpur: [23.1815, 79.9864],
  madurai: [9.9252, 78.1198],
  noida: [28.5355, 77.3910],
  gurgaon: [28.4595, 77.0266],
  gurugram: [28.4595, 77.0266],
};

/** Looks up a city's [lat, long] by name — case/whitespace-insensitive.
 * Returns null (never a guess) if the city isn't in the table. */
let manualByLoose = null;
export function getCityCoordinates(cityName) {
  if (!cityName) return null;
  if (!manualByLoose) {
    manualByLoose = new Map();
    Object.entries(CITY_COORDINATES).forEach(([name, coords]) => {
      const k = cityLooseKey(name.replace(/_/g, ' '));
      if (k && !manualByLoose.has(k)) manualByLoose.set(k, coords);
    });
  }
  // "JAIPUR-2", "Jaipur (Raj)", "JAIPUR *" all reduce to the same key.
  return manualByLoose.get(cityLooseKey(cityName)) || null;
}

/** Strips characters that sometimes ride along with a city name typed into
 * an Excel — a trailing "*", text in brackets like "MORBI (GUJ)", stray
 * punctuation, doubled spaces — so the name can be matched against the
 * coordinate tables. The original (uncleaned) name is still what gets
 * sent to the server; this is for matching and display only. */
export function cleanCityName(cityName) {
  if (!cityName) return '';
  return String(cityName)
    .replace(/\*/g, ' ')
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^\p{L}\p{N}\s.-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A forgiving comparison key for city names, so common spelling variants
 * land on the same key ("Bhubaneswar" / "Bhubaneshwar"): lowercase letters
 * only, 'h' dropped, 'w' read as 'v', repeated letters collapsed. */
export function cityLooseKey(cityName) {
  return cleanCityName(cityName)
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .replace(/h/g, '')
    .replace(/w/g, 'v')
    .replace(/(.)\1+/g, '$1');
}

export default CITY_COORDINATES;

/** Number of single-letter edits (insert/delete/change) between two words,
 * giving up early once it exceeds `max` — used to catch typos like
 * "Kishangrah" for "Kishangarh". */
export function editDistance(a, b, max = 2) {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i += 1) {
    const curr = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    prev = curr;
  }
  return prev[b.length];
}

const NAME_STOPWORDS = new Set([
  'industrial', 'industries', 'industry', 'estate', 'estates', 'area', 'phase', 'road', 'market',
  'nagar', 'colony', 'society', 'park', 'zone', 'sector', 'village', 'taluka', 'district', 'city',
  'town', 'highway', 'bypass', 'complex', 'ceramic', 'ceramics', 'tiles', 'marketing', 'traders',
  'trading', 'riico', 'gidc', 'midc',
]);

/** The meaningful words of a place name, longest first, for names like
 * "SITAPURA INDUSTRIAL AREA" or "BHIWADI RIICO" where only one word is the
 * actual place. Generic words (industrial, area, road…) are ignored. */
export function cityNameTokens(cityName) {
  return cleanCityName(cityName)
    .toLowerCase()
    .split(/[\s.-]+/)
    .filter((w) => /^[a-z]{5,}$/.test(w) && !NAME_STOPWORDS.has(w))
    .sort((a, b) => b.length - a.length);
}
